import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../../context/LanguageContext";
import { Expeditor, File, Order } from "../../../services";
import { WEIGHT_UNITS, getTransportTypeIcon } from "../../../mock";
import Loading from "../../../components/shared/Loading/Loading";
import CustomButton from "../../../components/custom/custom-button/CustomButton";
import ExistingOrderForm from "./ExistingOrderForm";
import { parseOrderForm } from "../utils/parseOrderForm";
import { parseDraftOrder } from "../utils/parseDraftOrder";
import { isRailwayOrder } from "../utils/railwayOrder";

export const loadExistingOrderForm = async (id, language, t, mode) => {
  const isDraft = mode === "draft";
  const isCopy = mode === "copy";
  const [
    orderResponse,
    countries,
    categories,
    types,
    temperatures,
    loadingTypes,
    partners,
    documents,
    invoices,
  ] = await Promise.all([
    isDraft ? Order.getDraftOrder(id) : Order.getOrderById(id, language),
    Order.getCountries(language),
    Order.getCategories(language),
    Order.getTypes(),
    Order.getTemperaturesDisc(),
    Order.getLoadingTypesDisc(language),
    Expeditor.getPartners(),
    File.getFilesByOrder(id, 5),
    File.getFilesByOrder(id, 0),
  ]);
  const draft = isDraft ? parseDraftOrder(orderResponse.data) : null;
  const order = draft ? draft.order : orderResponse.data;
  const isRailway = isRailwayOrder(order);
  const countryIds = [
    ...new Set(
      [
        order.countryIdFrom,
        order.countryIdTo,
        ...(order.addressList ?? []).map((point) => point.countryId),
      ].filter(Boolean),
    ),
  ];
  const [cargo, tenderPartners, cityResponses, wagonTypes] = await Promise.all([
    !isDraft && order.cargoId ? Order.getCargo(order.cargoId) : { data: {} },
    isDraft ? { data: draft.tenderPartners } : !isCopy && ["Tender", "Auction"].includes(order.assignmentMethod)
      ? Order.getTenderPartnersByOrder(id)
      : { data: [] },
    Promise.all(countryIds.map((countryId) => Order.getCities(language, countryId))),
    isRailway ? Order.getWagonTypes(language) : { data: [] },
  ]);
  const form = parseOrderForm(
    {
      order,
      cargo: cargo.data ?? {},
      tenderPartners: tenderPartners.data ?? [],
      documents: documents.data ?? [],
      invoices: invoices.data ?? [],
    },
    {
      countries: countries.data,
      categories: categories.data,
      types: types.data.map((type) => ({ ...type, icon: getTransportTypeIcon(type.value) })),
      temperatures: temperatures.data,
      loadingTypes: loadingTypes.data,
      partners: partners.data
        .filter((partner) => partner.state === "Confirmed" && partner.companyName)
        .map((partner) => ({ ...partner, name: partner.companyName })),
      citiesByCountry: Object.fromEntries(
        countryIds.map((countryId, index) => [countryId, cityResponses[index].data]),
      ),
      weightUnits: WEIGHT_UNITS(t),
      wagonTypes: wagonTypes.data ?? [],
    },
    { isCopy },
  );
  if (isDraft) return { ...form, draftId: id };
  if (isCopy) return { ...form, currency: null, acceptedCurrency: null };
  return form;
};

export default function ExistingOrderPage({ mode }) {
  const { id } = useParams();
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const [result, setResult] = useState(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setResult(null);
    setFailed(false);
    loadExistingOrderForm(id, currentLanguage, t, mode)
      .then((form) => {
        if (active) setResult({ id, language: currentLanguage, mode, form });
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [id, currentLanguage, t, attempt, mode]);

  if (failed)
    return (
      <div role="alert">
        <p>{t("unifiedOrder.loadError")}</p>
        <CustomButton text={t("authSession.retry")} onClick={() => setAttempt((value) => value + 1)} />
      </div>
    );
  if (!result || result.id !== id || result.language !== currentLanguage || result.mode !== mode) {
    return <Loading inBlock />;
  }
  return <ExistingOrderForm key={`${mode}:${id}:${currentLanguage}`} mode={mode} initialForm={result.form}
    onDraftSaved={() => setAttempt((value) => value + 1)} />;
}
